import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AUDIT_ACTOR, AUDIT_GROUPS, adminApi, formatIst } from '@wa/shared';
import type { AuditRow } from '@wa/shared';
import { Cell, DataTable, Empty, Page } from '@/components/Primitives';

/**
 * Who did what, to whom, and when.
 *
 * The table has existed since migration 0004 and held nothing until M8, so anything
 * before that date is genuinely absent rather than filtered out — which the page says
 * rather than showing an empty result and letting somebody conclude nothing happened.
 */
export function AuditPage() {
  const [group, setGroup] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [pages, setPages] = useState<(number | null)[]>([null]);

  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: adminApi.tenants });

  const beforeId = pages[pages.length - 1] ?? null;

  // Every field the query string reads is in the key, so a filter change refetches
  // and a stale page cannot be shown under a new filter.
  const trail = useQuery({
    queryKey: ['admin', 'audit', group, tenantId, from, to, beforeId],
    queryFn: () => adminApi.audit({
      // The group is a set of prefixes and the API takes one, so the common case —
      // a single prefix — filters on the server and the rest is narrowed here.
      action: prefixOf(group),
      tenantId: tenantId ? Number(tenantId) : null,
      from: from || null,
      to: to || null,
      beforeId,
      take: 50,
    }),
  });

  const rows = (trail.data?.rows ?? []).filter((row) => matchesGroup(row, group));

  const reset = (apply: () => void) => { apply(); setPages([null]); };

  return (
    <Page title="Audit" subtitle="Every permission, price, provisioning and bulk action, with who did it.">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Activity">
          <select
            value={group}
            onChange={(e) => reset(() => setGroup(e.target.value))}
            className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-body"
          >
            <option value="">Everything</option>
            {AUDIT_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </Field>

        <Field label="Client">
          <select
            value={tenantId}
            onChange={(e) => reset(() => setTenantId(e.target.value))}
            className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-body"
          >
            <option value="">Every client</option>
            {(tenants.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.companyName}</option>
            ))}
          </select>
        </Field>

        <Field label="From">
          <input type="date" value={from} onChange={(e) => reset(() => setFrom(e.target.value))}
                 className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-body" />
        </Field>

        <Field label="To">
          <input type="date" value={to} onChange={(e) => reset(() => setTo(e.target.value))}
                 className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-body" />
        </Field>

        {(group || tenantId || from || to) && (
          <button
            type="button"
            onClick={() => reset(() => { setGroup(''); setTenantId(''); setFrom(''); setTo(''); })}
            className="rounded border border-(--color-line) px-3 py-1.5 text-body text-(--color-muted) hover:text-(--color-ink)"
          >
            Clear
          </button>
        )}
      </div>

      <DataTable head={['When', 'Who', 'Did what', 'To', 'Client', 'Details']}>
        {rows.map((row) => (
          <tr key={row.id}>
            <Cell nowrap>{formatIst(row.createdAt)}</Cell>
            <Cell>
              <span className="block">{row.actorLabel ?? '—'}</span>
              <span className="text-micro text-(--color-muted)">{AUDIT_ACTOR[row.actorType] ?? 'Unknown'}</span>
            </Cell>
            <Cell mono nowrap>{row.action}</Cell>
            <Cell nowrap>
              {row.resourceType ? `${row.resourceType}${row.resourceId ? ` #${row.resourceId}` : ''}` : '—'}
            </Cell>
            <Cell>{row.tenantName ?? '—'}</Cell>
            <Cell><Detail json={row.detail} /></Cell>
          </tr>
        ))}
      </DataTable>

      {rows.length === 0 && !trail.isLoading && (
        <Empty>
          Nothing here. The trail starts when M8 was deployed — anything before that was
          never recorded, rather than filtered out.
        </Empty>
      )}

      <div className="mt-3 flex items-center gap-2">
        {pages.length > 1 && (
          <button
            type="button"
            onClick={() => setPages(pages.slice(0, -1))}
            className="rounded border border-(--color-line) px-3 py-1.5 text-body hover:text-(--color-ink)"
          >
            Back
          </button>
        )}
        {trail.data?.nextBeforeId && (
          <button
            type="button"
            onClick={() => setPages([...pages, trail.data!.nextBeforeId!])}
            className="rounded border border-(--color-line) px-3 py-1.5 text-body hover:text-(--color-ink)"
          >
            Older
          </button>
        )}
      </div>
    </Page>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="min-w-[9rem] flex-1 basis-40 text-small">
      <span className="mb-1 block text-(--color-muted)">{label}</span>
      {children}
    </label>
  );
}

/**
 * The detail as a short list of what changed, not as raw JSON.
 *
 * A trail nobody reads is a trail nobody checks, and `{"isActive":{"from":null,"to":false}}`
 * in a table cell is not read by anybody.
 */
function Detail({ json }: { json: string | null }) {
  if (!json) return <span className="text-(--color-muted)">—</span>;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return <span className="font-mono text-micro break-all">{json}</span>;
  }

  return (
    <ul className="space-y-0.5 text-small">
      {Object.entries(parsed).map(([key, value]) => (
        <li key={key}>
          <span className="text-(--color-muted)">{humanise(key)}: </span>
          {isChange(value)
            ? <span>{show(value.from)} <span className="text-(--color-muted)">→</span> {show(value.to)}</span>
            : <span>{show(value)}</span>}
        </li>
      ))}
    </ul>
  );
}

function isChange(value: unknown): value is { from: unknown; to: unknown } {
  return typeof value === 'object' && value !== null && 'from' in value && 'to' in value;
}

function show(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

function humanise(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

/** One prefix can go to the server; a group of several is narrowed here. */
function prefixOf(group: string): string | null {
  const found = AUDIT_GROUPS.find((g) => g.key === group);
  return found && found.prefixes.length === 1 ? found.prefixes[0]! : null;
}

function matchesGroup(row: AuditRow, group: string): boolean {
  if (!group) return true;
  const found = AUDIT_GROUPS.find((g) => g.key === group);
  return found ? found.prefixes.some((prefix) => row.action.startsWith(prefix)) : true;
}
